/**
 * Profanity Filter Service
 * 
 * Filters usernames and user-generated content for inappropriate language
 * Uses pattern matching to catch variations (l33t speak, spacing, etc.)
 */

export interface ProfanityCheckResult {
  clean: boolean;
  flagged: string[];
  sanitized: string;
}

// Comprehensive profanity list (expandable)
const PROFANITY_LIST = [
  // Common profanity
  'fuck', 'shit', 'bitch', 'ass', 'damn', 'hell', 'crap', 'piss', 'cock', 'dick',
  'pussy', 'cunt', 'bastard', 'whore', 'slut', 'faggot', 'fag', 'dyke', 'nigger',
  'nigga', 'retard', 'retarded', 'nazi', 'hitler',
  
  // Variations and compounds
  'motherfucker', 'asshole', 'shithead', 'dickhead', 'fucktard', 'dumbass', 'jackass',
  'bullshit', 'horseshit', 'bitchass', 'douchebag', 'dipshit', 'fuckface', 'shitface',
  
  // Sexual content
  'porn', 'sex', 'nude', 'naked', 'boob', 'tit', 'penis', 'vagina', 'anal',
  
  // Hate speech and slurs
  'nazi', 'hitler', 'kike', 'spic', 'chink', 'gook', 'wetback', 'beaner',
  
  // Violence and harm
  'kill', 'die', 'death', 'suicide', 'rape', 'murder',
];

// Character substitutions for l33t speak and variations
const LEETSPEAK_MAP: Record<string, string> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '8': 'b',
  '@': 'a',
  '$': 's',
  '!': 'i',
  '+': 't',
  '|': 'i',
  '*': 'a',
  '#': 'h',
};

/**
 * Normalize text to catch variations (remove spaces, l33t speak, etc.)
 */
function normalizeText(text: string): string {
  let normalized = text.toLowerCase();
  
  // Remove spaces, dots, dashes, underscores
  normalized = normalized.replace(/[\s._-]/g, '');
  
  // Replace l33t speak
  Object.keys(LEETSPEAK_MAP).forEach(key => {
    const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    normalized = normalized.replace(regex, LEETSPEAK_MAP[key]);
  });
  
  return normalized;
}

/**
 * Check if text contains profanity
 */
export function checkProfanity(text: string): ProfanityCheckResult {
  if (!text || text.trim().length === 0) {
    return { clean: true, flagged: [], sanitized: text };
  }

  const normalized = normalizeText(text);
  const flagged: string[] = [];
  
  // Check for exact matches and substrings
  for (const word of PROFANITY_LIST) {
    const normalizedWord = normalizeText(word);
    
    // Check if profanity appears as whole word or substring
    if (normalized.includes(normalizedWord)) {
      // Avoid false positives for common substrings
      // e.g., "assassin" contains "ass", "classic" contains "ass"
      const exceptions = ['classic', 'bass', 'class', 'grass', 'pass', 'mass', 'glass', 'brass', 'assassin', 'assumption'];
      const isException = exceptions.some(ex => normalizeText(ex).includes(normalizedWord) && normalized === normalizeText(ex));
      
      if (!isException) {
        flagged.push(word);
      }
    }
  }
  
  const clean = flagged.length === 0;
  const sanitized = clean ? text : '***';
  
  return { clean, flagged, sanitized };
}

/**
 * Validate username for profanity
 */
export function isUsernameClean(username: string): { clean: boolean; reason?: string } {
  const result = checkProfanity(username);
  
  if (!result.clean) {
    return {
      clean: false,
      reason: 'Username contains inappropriate language'
    };
  }
  
  return { clean: true };
}

/**
 * Check multiple fields for profanity (username, bio, etc.)
 */
export function checkUserContent(fields: Record<string, string>): {
  clean: boolean;
  flaggedFields: Record<string, string[]>;
} {
  const flaggedFields: Record<string, string[]> = {};
  
  Object.entries(fields).forEach(([fieldName, value]) => {
    if (value && value.trim().length > 0) {
      const result = checkProfanity(value);
      if (!result.clean) {
        flaggedFields[fieldName] = result.flagged;
      }
    }
  });
  
  return {
    clean: Object.keys(flaggedFields).length === 0,
    flaggedFields
  };
}
